import { ReactNode, useCallback, useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import { useLocation } from 'react-router'
import { GoogleOAuthProvider } from '@react-oauth/google'
import * as Sentry from '@sentry/react'
import SidebarMenu from './SidebarMenu/SidebarMenu'
import RedesignedSidebarMenu from './SidebarMenu/RedesignedSidebarMenu/RedesignedSidebarMenu'
import { StyledSidebarMenu } from './SidebarMenu/styled-components'
import { config } from '@/containers/Login/config'
import Login from '@/containers/Login'
import ErrorPage from '@/containers/ErrorPage'
import { selectAuthData } from '@/containers/Login/AuthSelectors'
import i18n from '@/i18n'
import { capitalize } from '@/shared/tools/stringFunctions'
import { applicationCountries } from '@/shared/tools/applicationCountries'
import { useNotificationReceivedListener } from './hooks/useNotificationReceivedListener'
import usePageBackground from './hooks/usePageBackground'
import useCutOffersListener from './hooks/useCutOffersListener'
import GamificationNotification from '@/shared/components/GamificationNotification/GamificationNotification'
import CallbackReminderPopup from '@/shared/components/CallbackReminderPopup/CallbackReminderPopup'
import CutOffersBanner from '@/shared/components/CutOffersBanner/CutOffersBanner'
import EnvironmentBanner from '@/shared/components/EnvironmentBanner/EnvironmentBanner'
import { hostEnvironment } from '@/shared/tools/hostEnvironment'
import HopTimusButton from './HopTimusButton/HopTimusButton'
import HopAssistanceChat from './HopAssistanceChat/HopAssistanceChat'
import {
  AppShell,
  AppShellWarning,
  BannerArea,
  SidebarArea,
  FloatingSidebarArea,
  MainContent,
  FloatingMainContent,
} from '@/shared/theme/styled-components'
import useLeftMenuRedesign from './SidebarMenu/useLeftMenuRedesign'
import useSidebarMenuExpanded from './SidebarMenu/useSidebarMenuExpanded'
import useRailSlotSize from './SidebarMenu/useRailSlotSize'
import useMenuWidthMotion from './SidebarMenu/useMenuWidthMotion'
import useBannerHeight from './hooks/useBannerHeight'

const COUNTRY_USING_ONLY_HOP_TIMUS = 'fr'

interface AuthData {
  authTokenSession: string | null
  isAdmin: boolean
  isSelectraAdmin: boolean
  country: string
  userLanguage: string
  applicationType: string
}

interface BackofficePageProps {
  children: ReactNode
}

const BackofficePage = ({ children }: BackofficePageProps) => {
  const {
    authTokenSession,
    isAdmin,
    isSelectraAdmin,
    country,
    userLanguage,
    applicationType: applicationTypeAuth,
  } = useSelector(selectAuthData) as AuthData
  const { gamificationNotification, clearGamificationNotification } = useNotificationReceivedListener()
  // Subscribe to the team-scoped Echo channel that pushes the live list of
  // offers currently in a cut period. Sleeps until teamId is available.
  useCutOffersListener()
  const floating = useLeftMenuRedesign()
  usePageBackground(floating)
  const sidebarMenu = useSidebarMenuExpanded()
  const menuExpanded = floating && sidebarMenu.expanded
  const slotSizeRef = useRailSlotSize(floating)
  const menuMotion = useMenuWidthMotion(floating, menuExpanded, sidebarMenu.toggle)
  const menuRef = useCallback(
    (menu: HTMLElement | null) => {
      slotSizeRef(menu)
      menuMotion.menuRef(menu)
    },
    [slotSizeRef, menuMotion.menuRef],
  )
  const bannerRef = useBannerHeight(floating)

  const locationHook = useLocation()
  const [loggedIn, setLoggedIn] = useState(!!authTokenSession)
  const [applicationType, setApplicationType] = useState(applicationTypeAuth)
  const authorizedApplicationTypes = [
    'telecom',
    'energie',
    'users-mngt',
    'teams-mngt',
    'suppliers-mngt',
    'italy-fiscal-codes-mngt',
    'websales-users-mngt',
    'ip-addresses-management',
    'assurance',
    'contact',
    'callbacks',
    'gestione-pause',
    'dev-tools-v2',
    'additional-sales',
    'organisations',
  ]
  const allowedCountries = Object.keys(applicationCountries)
  const hasErrorType = !authorizedApplicationTypes.includes(applicationType)
  const hasErrorCountry = !allowedCountries.includes(country)
  const environment = hostEnvironment()
  const Wrapper = environment && !floating ? AppShellWarning : AppShell
  const Sidebar = floating ? FloatingSidebarArea : SidebarArea
  const Main = floating ? FloatingMainContent : MainContent
  const Menu = floating ? RedesignedSidebarMenu : SidebarMenu

  useEffect(() => {
    if (!userLanguage || !country) return
    i18n.changeLanguage(`${country}${capitalize(userLanguage)}`)
  }, [userLanguage, country])

  useEffect(() => {
    if (authTokenSession) {
      setLoggedIn(true)
    } else {
      setLoggedIn(false)
    }
  }, [authTokenSession])

  useEffect(() => {
    const splitedPath = locationHook.pathname.split('/')
    const currentType = splitedPath[1] ?? ''

    if (applicationType !== currentType) {
      setApplicationType(currentType)
    }
  }, [locationHook.pathname])

  if (!loggedIn) {
    return (
      <GoogleOAuthProvider
        clientId={config.clientId}
        onScriptLoadError={() => {
          if (import.meta.env.PROD) {
            Sentry.captureException(new Error('Google OAuth script failed to load'))
          }
        }}
      >
        <Login />
      </GoogleOAuthProvider>
    )
  }

  if (hasErrorType || hasErrorCountry) {
    return (
      <Wrapper $redesign={floating}>
        <BannerArea ref={bannerRef}>
          <EnvironmentBanner environment={environment} hidden={!floating} />
          <CutOffersBanner />
        </BannerArea>
        <Sidebar id='sidebar-menu'>
          <StyledSidebarMenu ref={menuRef} data-rail-card $floating={floating} $expanded={menuExpanded}>
            <Menu
              applicationType={applicationType}
              isAdmin={isAdmin ?? false}
              isSelectraAdmin={isSelectraAdmin ?? false}
              error={true}
              expanded={menuExpanded}
              onToggle={menuMotion.toggle}
            />
          </StyledSidebarMenu>
        </Sidebar>
        <ErrorPage
          hasErrorType={hasErrorType}
          hasErrorCountry={hasErrorCountry}
          currentType={applicationType}
          currentCountry={country}
        />
      </Wrapper>
    )
  }

  return (
    <Wrapper $redesign={floating}>
      <BannerArea ref={bannerRef}>
        <EnvironmentBanner environment={environment} hidden={!floating} />
        <CutOffersBanner />
      </BannerArea>
      <Sidebar id='sidebar-menu'>
        <StyledSidebarMenu ref={menuRef} data-rail-card $floating={floating} $expanded={menuExpanded}>
          <Menu
            applicationType={applicationType}
            isAdmin={isAdmin ?? false}
            isSelectraAdmin={isSelectraAdmin ?? false}
            error={false}
            expanded={menuExpanded}
            onToggle={menuMotion.toggle}
          />
        </StyledSidebarMenu>
      </Sidebar>

      <Main id='main'>
        <section>{children}</section>
      </Main>
      <GamificationNotification notification={gamificationNotification} onClose={clearGamificationNotification} />
      <CallbackReminderPopup />
      <HopTimusButton />
      <HopAssistanceChat hidden={country === COUNTRY_USING_ONLY_HOP_TIMUS} />
    </Wrapper>
  )
}

export default BackofficePage
